import BaseListPresenter from "../../../base/BaseListPresenter";
import { findObjectUseCase } from "../../../usecases/object";
import {
  buildHierarchyWhere,
  findCurrentUserEmployee,
} from "../../../utils/hierarchy";

class LeaveCreditsPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.view = view;
  }

  init() {
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = [];
    this.keys = ["Firstname", "Middlename", "surname", "email", "agency"];
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  getIsScheduler() {
    const currentUser = this.view.getCurrentUser();
    const roles = currentUser?.roles || [];
    return roles.some((item) =>
      ["supervisor", "scheduler"].includes(item?.id?.toLowerCase()),
    );
  }

  createQuery(employee, isScheduler) {
    this.where = {};

    if (isScheduler && Array.isArray(employee) && employee.length > 0) {
      this.where = buildHierarchyWhere(employee[0]);
    }

    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    if (this.keys?.length > 0) {
      query.keys = this.keys;
    }
    return query;
  }

  async countObjects() {
    try {
      this.showProgress();
      const collection = this.view.getCollectionName();
      const employees = await findCurrentUserEmployee(
        findObjectUseCase(),
        this.view.getCurrentUser(),
      );
      const isScheduler = this.getIsScheduler();
      const query = this.createQuery(employees, isScheduler);

      let { count } = await this.countObjectUseCase.execute(collection, {
        where: query.where,
      });

      if (isScheduler) {
        const separated = await findObjectUseCase().execute("seperated", {
          keys: ["employee"],
        });
        count = Math.max(0, count - separated.length);
      }

      this.count = count;
      this.view.setCount(this.count);
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const employees = await findCurrentUserEmployee(
      findObjectUseCase(),
      this.view.getCurrentUser(),
    );
    const isScheduler = this.getIsScheduler();
    const query = this.createQuery(employees, isScheduler);

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);

      let filteredObjects = objects;
      if (isScheduler) {
        const separated = await findObjectUseCase().execute("seperated", {
          keys: ["employee"],
        });
        const separatedIds = separated.map((s) => s.employee?.id);
        filteredObjects = objects.filter((obj) => !separatedIds.includes(obj.id));
      }

      filteredObjects.forEach((obj) => {
        if (obj.employmentType?.types === "Plantilla") {
          obj.positionLabel = obj.position?.position_title || "";
        } else if (obj.employmentType?.types === "Non-Plantilla") {
          obj.positionLabel = obj.nonRegPosition?.positionTitle || "";
        }
      });

      this.objects = this.objects.concat(filteredObjects);
      this.view.setObjects(filteredObjects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.objects = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  async handleHierarchyChange(hierarchy) {
    const currentFilters = { ...this.view.state.filters };

    const formatter = {
      region: "regions",
      office: "offices",
      division: "divisions",
      unit: "units",
      cluster: "plantilla",
    };

    Object.entries(hierarchy).forEach(([key, value]) => {
      const mappedKey = formatter[key] ?? key;
      if (value) {
        currentFilters[mappedKey] = value;
      } else {
        delete currentFilters[mappedKey];
      }
    });

    this.view.setState(
      (prevState) => {
        const updatedFilters = { ...prevState.filters };

        Object.entries(hierarchy).forEach(([key, value]) => {
          const mappedKey = formatter[key] ?? key;
          if (!value) {
            delete updatedFilters[mappedKey];
          } else {
            updatedFilters[mappedKey] = value;
          }
        });

        return { filters: updatedFilters };
      },
      () => {
        this.view.filterSubmit(this.view.state.filters);
      },
    );
  }
}

export default LeaveCreditsPagePresenter;
